import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useMemo, useState } from "react";

export type TransportResourceFilter = "all" | "legs" | "circuits";

type TransportOverviewState = {
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    filter: TransportResourceFilter;
    setFilter: Dispatch<SetStateAction<TransportResourceFilter>>;
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    selectedIds: string[];
    setSelectedIds: Dispatch<SetStateAction<string[]>>;
    isMapExpanded: boolean;
    setIsMapExpanded: Dispatch<SetStateAction<boolean>>;
};

const TransportOverviewStateContext = createContext<TransportOverviewState | null>(null);

/**
 * Lifts the Transport overview UI state (selection, search, filter, page,
 * map panel) into a context owned by `TransportLayout`. The layout stays
 * mounted while the user navigates between the overview and the leg/circuit
 * edit pages, so the selection and filters are restored when the user comes
 * back via Save or Cancel — instead of being reset on every remount.
 */
export const TransportOverviewStateProvider = ({ children }: { children: ReactNode }) => {
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState<TransportResourceFilter>("all");
    const [page, setPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isMapExpanded, setIsMapExpanded] = useState(true);

    const value = useMemo<TransportOverviewState>(
        () => ({
            search,
            setSearch,
            filter,
            setFilter,
            page,
            setPage,
            selectedIds,
            setSelectedIds,
            isMapExpanded,
            setIsMapExpanded,
        }),
        [filter, isMapExpanded, page, search, selectedIds],
    );

    return <TransportOverviewStateContext.Provider value={value}>{children}</TransportOverviewStateContext.Provider>;
};

export const useTransportOverviewState = (): TransportOverviewState => {
    const value = useContext(TransportOverviewStateContext);
    if (value === null) {
        throw new Error("useTransportOverviewState must be used within a TransportOverviewStateProvider");
    }
    return value;
};
